import React, { useEffect, useMemo, useRef, useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MuiTooltip from '@mui/material/Tooltip';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useLocation } from 'react-router-dom';
import { useTenantNav } from '../hooks/useTenantNav';
import apiClient from '../api/client';
import { dashboardsApi, ExecutiveDashboardResponse, portfoliosApi, requestsApi, ppmProjectsApi, strategicCategoriesApi } from '../api/ppm';
import { useTenant } from '../contexts/TenantContext';
import { hasPageAccess } from '../utils/rbac';
import { PageHeader } from './layout/PageHeader';
import { StatCard } from './layout/StatCard';
import { TableCard } from './layout/TableCard';
import { PageLoader } from './PageLoader';
import { NexusDonut, NexusTrend, NexusBarChart } from './charts/NexusCharts';
import { computeFinance, formatCurrencyShort, formatSignedCurrencyShort, formatPercent } from '../utils/finance';
import { tokens } from '../theme/tokens';


const ExecutiveDashboard: React.FC = () => {
  const { dbUser } = useCurrentUser();
  const { navigate } = useTenantNav();
  const location = useLocation();
  const { currentTenant } = useTenant();
  const [data, setData] = useState<ExecutiveDashboardResponse | null>(null);
  const [portfolios, setPortfolios] = useState<any[]>([]);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]); // preview
  const [pendingRequestsAll, setPendingRequestsAll] = useState<any[]>([]); // full (up to limit)
  const [topRisks, setTopRisks] = useState<any[]>([]);
  const [overdueIssues, setOverdueIssues] = useState<any[]>([]);
  const [alignmentsSummary, setAlignmentsSummary] = useState<any>(null);
  const [topTemplates, setTopTemplates] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [topPanel, setTopPanel] = useState<null | 'portfolios' | 'projects' | 'risks' | 'requests'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState<string>('');
  const [savingBudget, setSavingBudget] = useState(false);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const topPanelAnchorRef = useRef<HTMLDivElement | null>(null);

  const canViewStrategic = hasPageAccess(dbUser, 'strategic-alignment');

  useEffect(() => {
    if (!currentTenant) return; // Wait for tenant to load
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await dashboardsApi.getExecutive();
        if (!mounted) return;
        setData(res.data);
        if (res.data?.financial?.companyBudget?.totalBudget) {
          setBudgetDraft(String(res.data.financial.companyBudget.totalBudget));
        }

        // Pull in richer widgets from existing APIs (no new backend required)
        const [pRes, pendingAllRes, risksRes, issuesRes, templatesRes, projectsRes, alignmentsRes] = await Promise.all([
          portfoliosApi.getAll(),
          requestsApi.getPending({ limit: 250 }),
          apiClient.get('/risks/top', { params: { limit: 6 } }),
          apiClient.get('/issues/overdue'),
          apiClient.get('/templates/most-used', { params: { limit: 6 } }),
          ppmProjectsApi.getAll(),
          canViewStrategic ? strategicCategoriesApi.portfolioSummary().catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        ]);
        if (!mounted) return;
        setPortfolios(pRes.data || []);
        const pendingAll = pendingAllRes.data || [];
        setPendingRequestsAll(pendingAll);
        setPendingRequests(pendingAll.slice(0, 6));
        setTopRisks(risksRes.data || []);
        setOverdueIssues((issuesRes.data || []).slice(0, 6));
        setTopTemplates(templatesRes.data || []);
        setProjects(projectsRes.data || []);
        setAlignmentsSummary((alignmentsRes as any)?.data || null);

      } catch (e: any) {
        if (!mounted) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load executive dashboard');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [currentTenant, canViewStrategic]);

  // Do not auto-scroll when opening top-panel drilldowns; keep users anchored at the top of the page.

  // (legacy chart datasets removed; enterprise-style KPI bars are rendered via SegmentedKpiBar now)

  const allocationPie = useMemo(() => {
    const list = data?.financial?.portfolioBreakdown || [];
    const companyUnallocated = data?.financial?.companyUnallocatedBudget ?? null;
    const rows = list
      .filter((p) => (p.budget || 0) > 0)
      .map((p) => ({ name: p.name, value: p.budget, utilization: p.utilization }));
    if (companyUnallocated != null && companyUnallocated > 0) {
      rows.push({ name: 'Unallocated', value: companyUnallocated, utilization: 0 });
    }
    // Keep chart readable: show top 8 + group the rest
    const sorted = rows.sort((a, b) => (b.value || 0) - (a.value || 0));
    const top = sorted.slice(0, 8);
    const rest = sorted.slice(8);
    const other = rest.reduce((s, r) => s + (r.value || 0), 0);
    return other > 0 ? [...top, { name: 'Other', value: other, utilization: 0 }] : top;
  }, [data]);

  const allocationDonut = useMemo(
    () => (allocationPie || []).map((d) => ({ name: String(d.name), value: Number(d.value || 0) })),
    [allocationPie]
  );

  const projectHealthSegments = useMemo(() => {
    if (!data) return { total: 0, segments: [] as Array<{ key: string; label: string; value: number; color: string }> };
    const total = Number(data.projects.total || 0);
    const onTrack = Number(data.projects.onTrack || 0);
    const atRisk = Number(data.projects.atRisk || 0);
    const offTrack = Number(data.projects.offTrack || 0);
    const other = Math.max(0, total - onTrack - atRisk - offTrack);
    const segments = [
      { key: 'onTrack', label: 'On track', value: onTrack, color: '#10B981' },
      { key: 'atRisk', label: 'At risk', value: atRisk, color: '#F59E0B' },
      { key: 'offTrack', label: 'Off track', value: offTrack, color: '#EF4444' },
      ...(other > 0 ? [{ key: 'other', label: 'Other', value: other, color: '#6B7280' }] : []),
    ].filter((s) => s.value > 0);
    return { total, segments };
  }, [data]);

  const SegmentedKpiBar: React.FC<{
    total: number;
    segments: Array<{ label: string; value: number; color: string }>;
    markerPct?: number | null;
  }> = ({ total, segments, markerPct }) => {
    const safeTotal = Math.max(0, Number(total || 0));
    const pct = (v: number) => (safeTotal > 0 ? (Number(v || 0) / safeTotal) * 100 : 0);
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: 14,
          borderRadius: 999,
          overflow: 'hidden',
          backgroundColor: 'rgba(17,24,39,0.08)',
          border: '1px solid rgba(17,24,39,0.10)',
        }}
      >
        <Box sx={{ display: 'flex', height: '100%' }}>
          {segments.map((s) => (
            <Box key={s.label} sx={{ width: `${pct(s.value)}%`, backgroundColor: s.color }} />
          ))}
        </Box>
        {markerPct != null && Number.isFinite(markerPct) ? (
          <Box
            sx={{
              position: 'absolute',
              left: `${Math.min(100, Math.max(0, markerPct))}%`,
              top: -3,
              bottom: -3,
              width: 2,
              backgroundColor: '#111827',
              opacity: 0.55,
            }}
          />
        ) : null}
      </Box>
    );
  };

  const fiscalSeries = useMemo(() => {
    const raw = data?.financial?.fiscalSeries || [];
    return raw.slice(-8);
  }, [data]);

  const companyBudgetMeta = useMemo(() => {
    const total = Number(data?.financial?.companyBudget?.totalBudget || 0);
    const allocatedToProjects = Number(data?.financial?.totalSpent || 0); // allocated-to-projects rollup
    const unallocated = Number(data?.financial?.companyUnallocatedBudget ?? 0); // can be negative if overallocated
    const allocated = total - unallocated; // can exceed total if overallocated
    const remaining = unallocated;
    const allocatedToProjectsPct = total > 0 ? Math.round((allocatedToProjects / total) * 100) : 0;
    const allocatedPct = total > 0 ? Math.round((allocated / total) * 100) : 0;
    return { total, allocatedToProjects, remaining, unallocated, allocated, allocatedToProjectsPct, allocatedPct };
  }, [data]);

  // Company-level budget model: Budget (ceiling) vs Allocated (commitment to projects = EAC)
  // vs Spent (actual cost to date). Variance = Budget − EAC.
  // NOTE: fiscalSeries[].totalCost is a CUMULATIVE running spend, so actual spend-to-date
  // is the LAST period's value — not the sum of every period (that would multi-count).
  const finance = useMemo(() => {
    const budget = Number(data?.financial?.companyBudget?.totalBudget || 0);
    const allocated = Number(data?.financial?.totalSpent || 0);
    const series = data?.financial?.fiscalSeries || [];
    const spent = series.length ? Number(series[series.length - 1]?.totalCost || 0) : 0;
    // Without a company budget set, fall back to the allocated rollup so variance reads as on-budget
    // rather than a misleading large negative.
    return computeFinance({ budget: budget || allocated, allocated, spent });
  }, [data]);

  const budgetChartData = useMemo(
    () => [
      { name: 'Budget', value: finance.budget },
      { name: 'Allocated', value: finance.allocated },
      { name: 'Spent', value: finance.spent },
      { name: 'EAC', value: finance.eac },
    ],
    [finance]
  );

  const alignmentRows = useMemo(() => {
    const rows = Array.isArray(alignmentsSummary?.rows) ? alignmentsSummary.rows : [];
    return rows
      .slice()
      .sort((a: any, b: any) => Number(b?.portfolioBudget || 0) - Number(a?.portfolioBudget || 0));
  }, [alignmentsSummary]);

  const moneyCompact = useMemo(() => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    });
  }, []);

  const money = useMemo(() => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  }, []);

  const saveCompanyBudget = async () => {
    const v = Number(String(budgetDraft || '').replace(/[, ]/g, ''));
    if (!Number.isFinite(v) || v <= 0) {
      setError('Company budget must be a positive number.');
      return;
    }
    try {
      setSavingBudget(true);
      setError(null);
      await apiClient.put('/financials/company-budget', { totalBudget: v, currency: 'USD' });
      const res = await dashboardsApi.getExecutive();
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to save company budget');
    } finally {
      setSavingBudget(false);
    }
  };

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="Overview"
        subtitle="Executive rollup across portfolios, projects, financials, RAID, and intake."
      />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {data ? (
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}>
            <StatCard title="Portfolios" value={data.portfolios.total} subtitle={`${data.portfolios.active} active`} onClick={() => setTopPanel((p) => (p === 'portfolios' ? null : 'portfolios'))} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Projects" value={data.projects.total} subtitle={`${data.projects.onTrack} on track • ${data.projects.atRisk} at risk`} onClick={() => setTopPanel((p) => (p === 'projects' ? null : 'projects'))} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Active Risks" value={data.raid.activeRisks} subtitle="Open / in work" onClick={() => setTopPanel((p) => (p === 'risks' ? null : 'risks'))} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard title="Pending Requests" value={pendingRequestsAll.length || data.requests.pending} subtitle="Submitted / in evaluation" onClick={() => setTopPanel((p) => (p === 'requests' ? null : 'requests'))} />
          </Grid>

          <Grid item xs={12}>
            <div ref={topPanelAnchorRef} />
            <Collapse in={!!topPanel} unmountOnExit>
              <TableCard
                title={
                  topPanel === 'portfolios'
                    ? 'Portfolios'
                    : topPanel === 'projects'
                      ? 'Projects'
                      : topPanel === 'risks'
                        ? 'Active Risks'
                        : 'Pending Requests'
                }
                actions={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)} sx={{ borderRadius: 2 }}>
                      {showAllPanel ? 'Show less' : 'Show all'}
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setTopPanel(null)} sx={{ borderRadius: 2 }}>
                      Close
                    </Button>
                  </Stack>
                }
              >
                  {topPanel === 'portfolios' ? (
                    <Stack spacing={1}>
                      {(portfolios || []).slice(0, showAllPanel ? 1000 : 5).map((p: any) => (
                        <Box
                          key={p.id}
                          sx={{ p: 1, borderRadius: 2, border: '1px solid #E5E7EB', cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' } }}
                          onClick={() => navigate(`/portfolios/${p.id}`)}
                        >
                          <Typography sx={{ fontWeight: 800 }} noWrap>
                            {p.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {p.status ? String(p.status) : '—'} • {p._count?.Projects ?? 0} projects
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}

                  {topPanel === 'projects' ? (
                    <Stack spacing={1}>
                      {(projects || []).slice(0, showAllPanel ? 1000 : 5).map((p: any) => (
                        <Box
                          key={p.id}
                          sx={{ p: 1, borderRadius: 2, border: '1px solid #E5E7EB', cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' } }}
                          onClick={() =>
                            navigate(p.jiraKey ? `/projects/${p.jiraKey}` : `/projects/internal/${p.id}`, { state: { from: location.pathname } })
                          }
                        >
                          <Typography sx={{ fontWeight: 800 }} noWrap>
                            {p.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {p.jiraKey ? `Jira: ${p.jiraKey}` : (p.name ? `Project: ${p.name}` : 'Project')} • {p.Portfolio?.name ? `Portfolio: ${p.Portfolio.name}` : 'No portfolio'}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}

                  {topPanel === 'risks' ? (
                    <Stack spacing={1}>
                      {(topRisks || []).slice(0, showAllPanel ? 1000 : 5).map((r: any) => (
                        <Box
                          key={r.id}
                          sx={{ p: 1, borderRadius: 2, border: '1px solid #E5E7EB', cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' } }}
                          onClick={() => navigate(`/raid?tab=risks&openId=${encodeURIComponent(r.id)}`)}
                        >
                          <Typography sx={{ fontWeight: 800 }} noWrap>
                            {r.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {r.status ? String(r.status) : '—'} • score {r.riskScore ?? '—'}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}

                  {topPanel === 'requests' ? (
                    <Stack spacing={1}>
                      {(pendingRequestsAll || []).slice(0, showAllPanel ? 1000 : 5).map((r: any) => (
                        <Box
                          key={r.id}
                          sx={{ p: 1, borderRadius: 2, border: '1px solid #E5E7EB', cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' } }}
                          onClick={() => navigate(`/requests?openId=${encodeURIComponent(r.id)}&showAll=1`)}
                        >
                          <Typography sx={{ fontWeight: 800 }} noWrap>
                            {r.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {r.priority ? String(r.priority) : '—'} • {r.department || '—'}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  ) : null}
              </TableCard>
            </Collapse>
          </Grid>

          {canViewStrategic ? (
            <>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 22 }}>
                          Budget
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Snapshot of spend, allocation, and burn. (Budget editing is in admin tools.)
                        </Typography>
                      </Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="flex-end">
                        <MuiTooltip
                          arrow
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 16 }}>
                                Company budget details
                              </Typography>
                              <Typography variant="body2">
                                <b>Allocated to projects:</b> {money.format(companyBudgetMeta.allocatedToProjects)}
                              </Typography>
                              <Typography variant="body2">
                                <b>Allocated to portfolios:</b> {money.format(companyBudgetMeta.allocated)}
                              </Typography>
                            </Box>
                          }
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Company budget: ${money.format(companyBudgetMeta.total)}`}
                            sx={{ fontWeight: 700, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                          />
                        </MuiTooltip>
                        {canViewStrategic ? (
                          <Button
                            variant="outlined"
                            onClick={() => setBudgetDialogOpen(true)}
                            sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                          >
                            Edit company budget
                          </Button>
                        ) : null}
                      </Stack>
                    </Stack>
                    <Divider sx={{ my: 1.75 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
                          <CardContent>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 16 }}>
                              Company budget utilization
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                              <Chip size="small" variant="outlined" label={`${companyBudgetMeta.allocatedPct}% allocated`} sx={{ fontWeight: 700 }} />
                              <Chip
                                size="small"
                                variant="outlined"
                                label={companyBudgetMeta.unallocated > 0 ? `${companyBudgetMeta.allocatedPct}% allocated` : 'Fully allocated'}
                                sx={{ fontWeight: 700 }}
                              />
                            </Stack>

                            <Box sx={{ mt: 1.25 }}>
                              <Typography variant="caption" color="text.secondary">
                                {companyBudgetMeta.remaining < 0 ? 'Overallocated' : 'Remaining'}
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                                {money.format(Math.abs(companyBudgetMeta.remaining))}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                of {money.format(companyBudgetMeta.total)} total
                              </Typography>
                            </Box>

                            <Box sx={{ mt: 1.25 }}>
                              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Allocated to projects
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  {money.format(companyBudgetMeta.allocatedToProjects)}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(100, companyBudgetMeta.allocatedToProjectsPct)}
                                sx={{ height: 10, borderRadius: 999 }}
                              />
                            </Box>

                            <Box sx={{ mt: 1 }}>
                              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Allocated to portfolios
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  {money.format(companyBudgetMeta.allocated)}
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(100, companyBudgetMeta.allocatedPct)}
                                sx={{ height: 10, borderRadius: 999 }}
                              />
                              {companyBudgetMeta.unallocated > 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                  Unallocated: {money.format(companyBudgetMeta.unallocated)}
                                </Typography>
                              ) : null}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent>
                            <Typography variant="subtitle2" sx={{ fontWeight: 650, fontSize: 16 }}>
                              Portfolio allocation
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              How the company budget is split across portfolios
                            </Typography>
                            <Box sx={{ mt: 1.5 }}>
                              <NexusDonut
                                data={allocationDonut}
                                valueFormatter={(v) => formatCurrencyShort(v)}
                                height={200}
                                centerLabel="Allocated"
                                centerValue={formatCurrencyShort(companyBudgetMeta.allocated)}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent>
                            <Typography variant="subtitle2" sx={{ fontWeight: 650, fontSize: 16 }}>
                              Burn over time
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Allocated vs. spend, last {fiscalSeries.length} periods
                            </Typography>
                            <Box sx={{ mt: 1.5 }}>
                              <NexusTrend
                                data={fiscalSeries}
                                xKey="period"
                                area
                                height={210}
                                valueFormatter={(v) => formatCurrencyShort(v)}
                                series={[
                                  { key: 'funding', name: 'Allocated', color: tokens.color.accent },
                                  { key: 'totalCost', name: 'Spend', color: tokens.color.danger },
                                ]}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Bottom row in the Budget Command Center */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
                      Project Health
                    </Typography>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Status distribution
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {projectHealthSegments.total} total
                      </Typography>
                    </Stack>

                    <SegmentedKpiBar total={projectHealthSegments.total} segments={projectHealthSegments.segments} />

                    <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                      {projectHealthSegments.segments.map((s) => {
                        const pct = projectHealthSegments.total > 0 ? Math.round((s.value / projectHealthSegments.total) * 100) : 0;
                        return (
                          <Stack key={s.key} direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: 999, backgroundColor: s.color }} />
                              <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                                {s.label}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                              {s.value} • {pct}%
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 650, fontSize: 18 }}>
                        Budget vs. actuals
                      </Typography>
                      <Chip
                        size="small"
                        label={`${finance.varianceLabel} ${formatSignedCurrencyShort(finance.variance)}`}
                        sx={{
                          fontWeight: 600,
                          color: finance.tone === 'unfavorable' ? tokens.color.danger : finance.tone === 'favorable' ? tokens.color.success : tokens.color.textSecondary,
                          bgcolor: finance.tone === 'unfavorable' ? tokens.color.dangerSoft : finance.tone === 'favorable' ? tokens.color.successSoft : tokens.color.neutralSoft,
                          border: 'none',
                        }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Variance = Budget − EAC. Favorable means under budget.
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      <NexusBarChart
                        data={budgetChartData}
                        xKey="name"
                        series={[{ key: 'value', name: 'Amount', color: tokens.color.accent }]}
                        valueFormatter={(v) => formatCurrencyShort(v)}
                        height={170}
                      />
                    </Box>

                    <Grid container spacing={1.25} sx={{ mt: 0.25 }}>
                      {[
                        { label: 'Budget', value: formatCurrencyShort(finance.budget), sub: 'Ceiling' },
                        { label: 'Allocated', value: formatCurrencyShort(finance.allocated), sub: 'Committed' },
                        { label: 'Spent', value: formatCurrencyShort(finance.spent), sub: `${formatPercent(finance.utilizationPct)} of budget` },
                        { label: 'ETC', value: formatCurrencyShort(finance.etc), sub: 'To complete' },
                        { label: 'EAC', value: formatCurrencyShort(finance.eac), sub: 'At completion' },
                        { label: 'Variance', value: formatSignedCurrencyShort(finance.variance), sub: finance.varianceLabel, tone: finance.tone },
                      ].map((m) => (
                        <Grid item xs={4} key={m.label}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {m.label}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: 16, color: (m as any).tone === 'unfavorable' ? tokens.color.danger : (m as any).tone === 'favorable' ? tokens.color.success : 'inherit' }}>
                            {m.value}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{m.sub}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : null}

          <Dialog open={budgetDialogOpen} onClose={() => setBudgetDialogOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>Edit company budget</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                This is typically set infrequently. It affects remaining/unallocated calculations in Overview + Portfolios.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Company budget (USD)"
                value={budgetDraft}
                onChange={(e) => setBudgetDraft(e.target.value)}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setBudgetDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2 }}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await saveCompanyBudget();
                  setBudgetDialogOpen(false);
                }}
                disabled={savingBudget}
                variant="contained"
                sx={{ borderRadius: 2 }}
              >
                {savingBudget ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Integrations section removed from Overview */}

          <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
            <Card variant="outlined" sx={{ flex: 1, height: 360 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 18 }}>
                    Portfolios
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => navigate('/portfolios')}>
                    View all
                  </Button>
                </Box>
                <Divider sx={{ my: 1.25 }} />
                <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', pr: 1 }}>
                  {(portfolios || []).slice(0, 6).map((p: any) => {
                    const projectsForPortfolio = Array.isArray(p?.Projects) ? p.Projects : [];
                    // Keep this consistent with the Portfolios page "Spend %" metric:
                    // percent allocated-to-projects (sum of project.budgetedCost) vs portfolio.totalBudget.
                    const derivedAllocated = projectsForPortfolio.reduce((s: number, x: any) => s + Number(x?.budgetedCost || 0), 0);
                    const budget = Number(p.totalBudget || 0);
                    const allocated = derivedAllocated || Number(p.totalSpent || 0);
                    const pct = budget ? Math.round((allocated / budget) * 100) : 0;
                    return (
                      <Box
                        key={p.id}
                        sx={{
                          display: 'flex',
                          gap: 1.25,
                          alignItems: 'center',
                          borderRadius: 2,
                          p: 1,
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800 }} noWrap>
                            {p.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {p.status ? String(p.status) : '—'} • {p._count?.Projects ?? 0} projects
                          </Typography>
                        </Box>
                        <Box sx={{ width: 160 }}>
                          <Typography variant="caption" color="text.secondary">
                            Spend {pct}%
                          </Typography>
                          <LinearProgress variant="determinate" value={Math.min(100, pct)} sx={{ height: 8, borderRadius: 999 }} />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
            <Card variant="outlined" sx={{ flex: 1, height: 360 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 18 }}>
                    Pending intake
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => { setShowAllPanel(true); setTopPanel('requests'); }}>
                    Show all
                  </Button>
                </Box>
                <Divider sx={{ my: 1.25 }} />
                <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', pr: 1 }}>
                  {(pendingRequests || []).slice(0, 6).map((r: any) => (
                    <Box
                      key={r.id}
                      sx={{ p: 1, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' } }}
                    >
                      <Typography sx={{ fontWeight: 800 }} noWrap>
                        {r.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {r.priority ? String(r.priority) : '—'} • {r.department || '—'}
                      </Typography>
                    </Box>
                  ))}
                  {(pendingRequests || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No pending requests.
                    </Typography>
                  ) : null}
                  {pendingRequestsAll.length > pendingRequests.length ? (
                    <Typography variant="caption" color="text.secondary">
                      Showing {pendingRequests.length} of {pendingRequestsAll.length}
                    </Typography>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 18 }}>
                    Top risks
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => navigate('/raid')}>
                    Open RAID
                  </Button>
                </Box>
                <Divider sx={{ my: 1.25 }} />
                <Stack spacing={1}>
                  {(topRisks || []).slice(0, 6).map((risk: any) => (
                    <Box
                      key={risk.id}
                      sx={{ p: 1, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' } }}
                    >
                      <Typography sx={{ fontWeight: 800 }} noWrap>
                        {risk.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {risk.status ? String(risk.status) : '—'} • score {risk.riskScore ?? '—'}
                      </Typography>
                    </Box>
                  ))}
                  {(topRisks || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No risks found.
                    </Typography>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 18 }}>
                    Overdue issues
                  </Typography>
                  <Divider sx={{ my: 1.25 }} />
                  <Stack spacing={1}>
                    {(overdueIssues || []).slice(0, 6).map((issue: any) => (
                      <Box key={issue.id} sx={{ p: 1, borderRadius: 2, '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' } }}>
                        <Typography sx={{ fontWeight: 800 }} noWrap>
                          {issue.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {issue.status ? String(issue.status) : '—'} • due {issue.dueDate ? String(issue.dueDate).slice(0, 10) : '—'}
                        </Typography>
                      </Box>
                    ))}
                    {(overdueIssues || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No overdue issues.
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>

              {canViewStrategic ? (
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 18 }}>
                        Strategic alignments
                      </Typography>
                      <Button size="small" variant="outlined" onClick={() => navigate('/strategic-alignment')}>
                        View all
                      </Button>
                    </Box>
                    <Divider sx={{ my: 1.25 }} />
                    <Stack spacing={1} sx={{ maxHeight: 280, overflowY: 'auto', pr: 1 }}>
                      {(alignmentRows || []).slice(0, 8).map((r: any) => {
                        const color = String(r?.color || '').trim() || '#3B82F6';
                        return (
                          <Box
                            key={r.id}
                            onClick={() => navigate(`/strategic-alignment?openId=${encodeURIComponent(String(r.id))}`)}
                            sx={{ p: 1, borderRadius: 2, cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' } }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: 999, backgroundColor: color }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700 }} noWrap title={r.name}>
                                  {r.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {Number(r.portfoliosCount || 0)} portfolios • {Number(r.projectsCount || 0)} projects • {moneyCompact.format(Number(r.portfolioBudget || 0))} budget
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        );
                      })}
                      {(alignmentRows || []).length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No strategic alignments found.
                        </Typography>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 18 }}>
                    Templates (most used)
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => navigate('/templates')}>
                    Manage templates
                  </Button>
                </Box>
                <Divider sx={{ my: 1.25 }} />
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  {(topTemplates || []).slice(0, 6).map((t: any) => (
                    <Chip key={t.id} label={t.name} sx={{ mb: 1 }} />
                  ))}
                  {(topTemplates || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No templates found.
                    </Typography>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}
      </Container>
    </PageLoader>
  );
};

export default ExecutiveDashboard;


